import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";
import DatePicker from "../Common/DatePicker";
import { displayLabel, employeeDisplayName } from "../../utils/displayLabel";
import {
  employeeService,
  branchService,
  departmentService,
  designationService,
  gradesService,
  categoryService,
  subDepartmentService,
} from "../../services/api";
import { DsButton, DsIcon } from "../../design-system";

const extractEmployeesFromResponse = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.data)) return resp.data;
  if (Array.isArray(resp.employees)) return resp.employees;
  if (resp.success && Array.isArray(resp.data)) return resp.data;
  if (resp.data && Array.isArray(resp.data.employees)) return resp.data.employees;
  if (resp.data && Array.isArray(resp.data.data)) return resp.data.data;
  return [];
};

const normalizeEmployeeOption = (employee) => {
  const id = employee?.id ?? employee?.employeeId ?? employee?.userId ?? null;
  if (id == null) return null;
  const name = employee?.user?.name || employee?.name || "Unknown";
  const empCode = employee?.empCode || employee?.employeeId || "";
  const email = employee?.user?.email || employee?.email || "";
  return {
    id: String(id),
    name: String(name),
    empCode: String(empCode || ""),
    email: String(email || ""),
  };
};

const formatEmployeeOptionLabel = (emp) => {
  const codePart = emp?.empCode ? `${emp.empCode} - ` : "";
  const emailPart = emp?.email ? ` (${emp.email})` : "";
  return `${codePart}${emp?.name || "Unknown"}${emailPart}`;
};

const MIN_EMPLOYEE_QUERY_LEN = 2;
const MULTI_SELECT_ALL_VALUE = "__all__";
const MULTI_SELECT_FIELDS = [
  "branch",
  "department",
  "designation",
  "grade",
  "category",
  "subDepartment",
  "employmentType",
  "status",
];

const normalizeMultiSelectValues = (value) => {
  const source = Array.isArray(value)
    ? value
    : value === undefined || value === null || value === ""
      ? []
      : [value];
  const normalized = source
    .map((item) => String(item).trim())
    .filter(Boolean);
  if (normalized.includes(MULTI_SELECT_ALL_VALUE)) {
    return [MULTI_SELECT_ALL_VALUE];
  }
  return [...new Set(normalized)];
};


export default function Filters({
  onApply,
  onClose,
  onClear,
  darkMode = false,
  initialFilters = {},
  payslipMonthYear,
}) {
  // Filter state (default to 'all' for dateFilterType so date inputs start disabled)
  const [filters, setFilters] = useState({
    dateFilterType: "all",
    branch: [],
    department: [],
    designation: [],
    grade: [],
    category: [],
    subDepartment: [],
    employmentType: [],
    status: [],
    selectedEmployees: [],
    selectedEmployeeIds: [],
    ...initialFilters,
  });
  // Guard to avoid duplicate loads in React 18 Strict Mode (development)
  const loadedRef = useRef(false);


  // Master data states
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [grades, setGrades] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);


  // UI states
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [employeeSuggestions, setEmployeeSuggestions] = useState([]);
  const [showEmployeeSuggestions, setShowEmployeeSuggestions] = useState(false);
  const [employeeSuggestionsLoading, setEmployeeSuggestionsLoading] = useState(false);
  const [activeEmployeeSuggestionIndex, setActiveEmployeeSuggestionIndex] =
    useState(-1);
  const [openMultiSelectKey, setOpenMultiSelectKey] = useState(null);
  const employeeSearchDebounceRef = useRef(null);
  const employeePickerRef = useRef(null);


  // Load master data on component mount (once)
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadMasterData();
  }, []);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (
        employeePickerRef.current &&
        !employeePickerRef.current.contains(event.target)
      ) {
        setShowEmployeeSuggestions(false);
        setActiveEmployeeSuggestionIndex(-1);
      }
      if (!event.target.closest?.("[data-multi-select-root='true']")) {
        setOpenMultiSelectKey(null);
      }
    };
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, []);

  useEffect(() => {
    const selectedEmployees = Array.isArray(initialFilters?.selectedEmployees)
      ? initialFilters.selectedEmployees
      : [];
    const selectedIds = Array.isArray(initialFilters?.selectedEmployeeIds)
      ? initialFilters.selectedEmployeeIds.map((id) => String(id))
      : [];

    if (selectedEmployees.length > 0 && selectedIds.length === 0) {
      const normalized = selectedEmployees
        .map(normalizeEmployeeOption)
        .filter(Boolean);
      const deduped = [];
      const seen = new Set();
      for (const emp of normalized) {
        if (seen.has(emp.id)) continue;
        seen.add(emp.id);
        deduped.push(emp);
      }
      setFilters((prev) => ({
        ...prev,
        selectedEmployees: deduped,
        selectedEmployeeIds: deduped.map((emp) => emp.id),
      }));
      return;
    }
    if (selectedEmployees.length > 0 && selectedIds.length > 0) return;
    if (selectedIds.length === 0) return;

    // Backward compatibility for older saved filter snapshots that only contain IDs.
    const hydrated = selectedIds.map((id) => ({
      id,
      name: `Employee ${id}`,
      empCode: "",
      email: "",
    }));
    setFilters((prev) => ({
      ...prev,
      selectedEmployees: hydrated,
      selectedEmployeeIds: hydrated.map((emp) => emp.id),
    }));
  }, [initialFilters]);

  useEffect(() => {
    setFilters((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const field of MULTI_SELECT_FIELDS) {
        const normalized = normalizeMultiSelectValues(prev[field]);
        const current = Array.isArray(prev[field]) ? prev[field] : [];
        if (
          normalized.length !== current.length ||
          normalized.some((value, index) => value !== String(current[index]))
        ) {
          next[field] = normalized;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [initialFilters]);


  const loadMasterData = async () => {
    try {
      setLoading(true);
      const [
        branchesRes,
        departmentsRes,
        designationsRes,
        gradesRes,
        categoriesRes,
        subDepartmentsRes,
        reportingManagersRes,
      ] = await Promise.all([
        branchService.getBranches(),
        departmentService.getDepartments(),
        designationService.getDesignations(),
        gradesService.getGrades(),
        categoryService.getAllCategories(),
        subDepartmentService.getAllSubDepartments(),
        employeeService.getReportingEmployees(),
      ]);


      setBranches(branchesRes.data || []);
      setDepartments(departmentsRes.data || []);
      setDesignations(designationsRes.data || []);
      setGrades(gradesRes.data || []);
      setCategories(categoriesRes.data || []);
      setSubDepartments(subDepartmentsRes.data || []);
      setReportingManagers(
        reportingManagersRes.data || reportingManagersRes || []
      );
    } catch (error) {
      console.error("Error loading master data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const query = String(filters.search || "").trim();
    if (employeeSearchDebounceRef.current) {
      clearTimeout(employeeSearchDebounceRef.current);
    }

    if (query.length < MIN_EMPLOYEE_QUERY_LEN) {
      setEmployeeSuggestions([]);
      setEmployeeSuggestionsLoading(false);
      setShowEmployeeSuggestions(false);
      setActiveEmployeeSuggestionIndex(-1);
      return;
    }
    setShowEmployeeSuggestions(true);
    setActiveEmployeeSuggestionIndex(-1);

    employeeSearchDebounceRef.current = setTimeout(async () => {
      try {
        setEmployeeSuggestionsLoading(true);
        let response = await employeeService.getEmployees({
          search: query,
          page: 1,
          limit: 20,
        });
        let all = extractEmployeesFromResponse(response)
          .map(normalizeEmployeeOption)
          .filter(Boolean);
        // Fallback for endpoints that don't search all relevant fields consistently.
        // We only fetch a limited sample to keep this responsive.
        if (all.length === 0) {
          response = await employeeService.getEmployees({
            page: 1,
            limit: 200,
          });
          all = extractEmployeesFromResponse(response)
            .map(normalizeEmployeeOption)
            .filter(Boolean);
        }
        const queryLower = query.toLowerCase();
        const selectedSet = new Set(
          (filters.selectedEmployeeIds || []).map((id) => String(id))
        );
        const filtered = all.filter((emp) => {
          if (selectedSet.has(String(emp.id))) return false;
          const haystack = `${emp.name} ${emp.empCode} ${emp.email}`.toLowerCase();
          return haystack.includes(queryLower);
        });
        setEmployeeSuggestions(filtered);
        setActiveEmployeeSuggestionIndex(filtered.length ? 0 : -1);
      } catch (error) {
        console.error("Error searching employees:", error);
        setEmployeeSuggestions([]);
        setActiveEmployeeSuggestionIndex(-1);
      } finally {
        setEmployeeSuggestionsLoading(false);
      }
    }, 250);

    return () => {
      if (employeeSearchDebounceRef.current) {
        clearTimeout(employeeSearchDebounceRef.current);
      }
    };
  }, [filters.search, filters.selectedEmployeeIds]);


  // Validation functions from OrgDetailsForm
  const validateDateRange = (from, to) => {
    if (!from && !to) return null;


    if (from && to) {
      const fromD = new Date(from);
      const toD = new Date(to);


      if (isNaN(fromD.getTime()) || isNaN(toD.getTime())) {
        return "Invalid date format";
      }


      if (fromD && fromD > toD) {
        return "From date cannot be greater than To date";
      } else if (toD && fromD > toD) {
        return "To date cannot be less than From date";
      }
    }


    return null;
  };


  const validateCTCRange = (ctcFrom, ctcTo) => {
    if (!ctcFrom && !ctcTo) return null;


    // Check for negative values
    if (
      (ctcFrom && parseFloat(ctcFrom) < 0) ||
      (ctcTo && parseFloat(ctcTo) < 0)
    ) {
      return "CTC cannot be negative";
    }


    // Check if both values are provided and validate range
    if (ctcFrom && ctcTo) {
      const from = parseFloat(ctcFrom);
      const to = parseFloat(ctcTo);


      if (isNaN(from) || isNaN(to)) {
        return "CTC values must be valid numbers";
      }


      if (from > to) {
        return "CTC From must be less than or equal to CTC To";
      }
    }


    // Check individual values
    if (ctcFrom && parseFloat(ctcFrom) > 999999999) {
      return "CTC From cannot exceed 999,999,999";
    }


    if (ctcTo && parseFloat(ctcTo) > 999999999) {
      return "CTC To cannot exceed 999,999,999";
    }


    return null;
  };


  const validateNumericId = (value, fieldName) => {
    const values = Array.isArray(value) ? value : [value];
    const filtered = values
      .map((v) => String(v).trim())
      .filter((v) => v && v !== MULTI_SELECT_ALL_VALUE);
    if (filtered.length === 0) return null;
    const hasInvalid = filtered.some((v) => isNaN(v) || Number(v) < 0);
    if (hasInvalid) {
      return `${fieldName} must be a valid positive number`;
    }
    return null;
  };


  // Comprehensive validation function
  const validateFilters = (filtersObj) => {
    const errors = {};


    // Date range validation
    if (filtersObj.dateFilterType && filtersObj.dateFilterType !== "all") {
      const dateError = validateDateRange(
        filtersObj.reportDateFrom,
        filtersObj.reportDateTo
      );
      if (dateError) {
        errors.dateError = dateError;
      }
    }


    // CTC range validation
    const ctcError = validateCTCRange(filtersObj.ctcFrom, filtersObj.ctcTo);
    if (ctcError) {
      errors.ctcError = ctcError;
    }


    // Numeric ID validations
    if (filtersObj.branch) {
      const branchError = validateNumericId(filtersObj.branch, "Branch");
      if (branchError) errors.branch = branchError;
    }


    if (filtersObj.department) {
      const deptError = validateNumericId(filtersObj.department, "Department");
      if (deptError) errors.department = deptError;
    }


    if (filtersObj.designation) {
      const designationError = validateNumericId(
        filtersObj.designation,
        "Designation"
      );
      if (designationError) errors.designation = designationError;
    }


    if (filtersObj.grade) {
      const gradeError = validateNumericId(filtersObj.grade, "Grade");
      if (gradeError) errors.grade = gradeError;
    }


    if (filtersObj.category) {
      const categoryError = validateNumericId(filtersObj.category, "Category");
      if (categoryError) errors.category = categoryError;
    }


    if (filtersObj.subDepartment) {
      const subDeptError = validateNumericId(
        filtersObj.subDepartment,
        "Sub Department"
      );
      if (subDeptError) errors.subDepartment = subDeptError;
    }


    return errors;
  };


  const handleChange = (key) => (e) => {
    let value = e.target.value;


    // Special handling for CTC fields
    if (key === "ctcFrom" || key === "ctcTo") {
      if (value === "" || !isNaN(value)) {
        const nextFilters = { ...filters, [key]: value };
        setFilters(nextFilters);
        // Validate on change if field has been touched
        if (touchedFields[key]) {
          const errors = validateFilters(nextFilters);
          setFormErrors(errors);
        }
        return;
      }
    }


    // Keep search input natural while typing; other string fields can be trimmed.
    if (typeof value === "string" && key !== "search") value = value.trim();
    const nextFilters = { ...filters, [key]: value };
    setFilters(nextFilters);
    // Validate on change if field has been touched
    if (touchedFields[key]) {
      const errors = validateFilters(nextFilters);
      setFormErrors(errors);
    }


    // Special handling for date fields - validate date range when either changes
    if (
      (key === "reportDateFrom" ||
        key === "reportDateTo" ||
        key === "dateFilterType") &&
      (touchedFields.reportDateFrom || touchedFields.reportDateTo)
    ) {
      const errors = validateFilters(nextFilters);
      setFormErrors(errors);
    }
  };

  const setMultiSelectValues = (key, values) => {
    const normalized = normalizeMultiSelectValues(values);
    const nextFilters = { ...filters, [key]: normalized };
    setFilters(nextFilters);
    if (touchedFields[key]) {
      const errors = validateFilters(nextFilters);
      setFormErrors(errors);
    }
  };

  const toggleMultiSelectValue = (key, value) => {
    const selectedValues = normalizeMultiSelectValues(filters[key]);
    const selectedSet = new Set(selectedValues);
    const isAllValue = value === MULTI_SELECT_ALL_VALUE;
    if (isAllValue) {
      setMultiSelectValues(key, [MULTI_SELECT_ALL_VALUE]);
      return;
    }

    selectedSet.delete(MULTI_SELECT_ALL_VALUE);
    if (selectedSet.has(value)) selectedSet.delete(value);
    else selectedSet.add(value);
    setMultiSelectValues(key, [...selectedSet]);
  };

  const setSelectedEmployees = (employees, options = {}) => {
    const { clearSearch = false } = options;
    const normalized = (Array.isArray(employees) ? employees : [])
      .map(normalizeEmployeeOption)
      .filter(Boolean);
    const deduped = [];
    const seen = new Set();
    for (const emp of normalized) {
      if (seen.has(emp.id)) continue;
      seen.add(emp.id);
      deduped.push(emp);
    }
    setFilters((prev) => ({
      ...prev,
      selectedEmployees: deduped,
      selectedEmployeeIds: deduped.map((emp) => emp.id),
      ...(clearSearch ? { search: "" } : {}),
    }));
  };

  const addSelectedEmployee = (employee) => {
    const normalized = normalizeEmployeeOption(employee);
    if (!normalized) return;
    const exists = (filters.selectedEmployeeIds || []).some(
      (id) => String(id) === normalized.id
    );
    if (exists) {
      setShowEmployeeSuggestions(false);
      setActiveEmployeeSuggestionIndex(-1);
      return;
    }
    setSelectedEmployees([...(filters.selectedEmployees || []), normalized], {
      clearSearch: true,
    });
    setEmployeeSuggestions([]);
    setShowEmployeeSuggestions(false);
    setActiveEmployeeSuggestionIndex(-1);
  };

  const removeSelectedEmployee = (idToRemove) => {
    const remaining = (filters.selectedEmployees || []).filter(
      (emp) => String(emp.id) !== String(idToRemove)
    );
    setSelectedEmployees(remaining);
  };


  // Handle field blur
  const handleFieldBlur = (fieldName) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));


    const errors = validateFilters(filters);
    setFormErrors(errors);
  };


  // Helper to transform filter object into API params
  const buildApiParamsFromFilters = (filtersObj) => {
    const params = {};
    if (!filtersObj) return params;


    // Map of filter keys to API param keys and their type
    const mappings = [
      ["branch", "branchId", "number"],
      ["department", "departmentId", "number"],
      ["designation", "designationId", "number"],
      ["grade", "gradeId", "number"],
      ["category", "categoryId", "number"],
      ["subDepartment", "subDepartmentId", "number"],
      ["employmentType", "employmentType", "string"],
      ["status", "employmentStatus", "string"],
      ["pfApplicable", "pfApplicable", "boolean"],
      ["ceilingPf", "ceilingPf", "boolean"],
      ["esiApplicable", "esiApplicable", "boolean"],
      ["ptApplicable", "ptApplicable", "boolean"],
      ["gratuityApplicable", "gratuityApplicable", "boolean"],
      ["epsApplicable", "epsApplicable", "boolean"],
      ["hasUAN", "hasUAN", "boolean"],
      ["hasESIIP", "hasESIIP", "boolean"],
      ["ctcFrom", "ctcFrom", "number"],
      ["ctcTo", "ctcTo", "number"],
      ["dateFilterType", "dateFilterType", "string"],
      ["reportingManagerId", "reportingManagerId", "number"],
      ["gender", "gender", "string"],
      ["userStatus", "userStatus", "string"],
      ["bloodGroup", "bloodGroup", "string"],
      ["search", "search", "string"],
    ];


    mappings.forEach(([key, apiKey, type]) => {
      let val = filtersObj[key];
      if (val === undefined || val === null || val === "") return;
      if (Array.isArray(val)) {
        const normalized = normalizeMultiSelectValues(val);
        if (normalized.length === 0) return;
        if (normalized.includes(MULTI_SELECT_ALL_VALUE)) return;

        if (type === "number") {
          const nums = normalized
            .map((item) => Number(item))
            .filter((num) => !Number.isNaN(num));
          if (nums.length === 0) return;
          params[apiKey] = nums.length === 1 ? nums[0] : nums.join(",");
          return;
        }

        const strings = normalized
          .map((item) => String(item).trim())
          .filter(Boolean);
        if (strings.length === 0) return;
        params[apiKey] = strings.length === 1 ? strings[0] : strings.join(",");
        return;
      }
      if (type === "boolean") {
        if (val === true || val === false) params[apiKey] = val;
        else if (val === "true" || val === "false")
          params[apiKey] = val === "true";
      } else if (type === "number") {
        const n = Number(val);
        if (!Number.isNaN(n)) params[apiKey] = n;
      } else {
        const normalized = typeof val === "string" ? val.trim() : val;
        // Keep parity with Employee filter behavior:
        // "all" is a default/non-filter value and should not be sent.
        if (normalized === "" || normalized === "all") return;
        params[apiKey] = normalized;
      }
    });


    // Date range only when a specific date type is selected
    const dType = filtersObj.dateFilterType;
    const from = filtersObj.reportDateFrom;
    const to = filtersObj.reportDateTo;
    if (dType && dType !== "all") {
      if (from) params.dateFrom = from;
      if (to) params.dateTo = to;
    }

    const selectedIds = Array.isArray(filtersObj.selectedEmployeeIds)
      ? filtersObj.selectedEmployeeIds
          .map((id) => String(id).trim())
          .filter(Boolean)
      : [];
    if (selectedIds.length > 0) {
      // CSV is safer for broad backend compatibility.
      params.employeeIds = selectedIds.join(",");
      // Array form is useful for screens that consume params directly in UI logic.
      params.selectedEmployeeIds = selectedIds;
    }


    return params;
  };


  const handleClear = () => {
    setFilters({
      // date filter
      dateFilterType: "all",
      reportDateFrom: "",
      reportDateTo: "",


      // master data
      branch: [],
      department: [],
      designation: [],
      grade: [],
      category: [],
      subDepartment: [],


      // employment details
      employmentType: [],
      status: [],
      reportingManagerId: "",
      gender: "",
      userStatus: "",
      bloodGroup: "",
      search: "",
      selectedEmployees: [],
      selectedEmployeeIds: [],


      // statutory applicability
      pfApplicable: "",
      esiApplicable: "",
      ptApplicable: "",
      gratuityApplicable: "",
      epsApplicable: "",
      ceilingPf: "",
      hasUAN: "",
      hasESIIP: "",


      // CTC
      ctcFrom: "",
      ctcTo: "",
    });


    setFormErrors({});
    setTouchedFields({});
    setEmployeeSuggestions([]);
    setShowEmployeeSuggestions(false);
    setActiveEmployeeSuggestionIndex(-1);
    setOpenMultiSelectKey(null);


    if (typeof onClear === "function") {
      onClear();
    }
  };


  const handleApply = async () => {
    // Mark all fields as touched to show all errors
    const allFields = [
      "branch",
      "department",
      "designation",
      "grade",
      "category",
      "subDepartment",
      "reportDateFrom",
      "reportDateTo",
      "ctcFrom",
      "ctcTo",
      "dateFilterType",
      "EmploymentType",
      "EmploymentStatus",
      "Gender",
      "UserStatus",
      "BloodGroup",
      "Search",
      "ReportingManagerId",
      "pfApplicable",
      "ceilingPf",
      "esiApplicable",
      "ptApplicable",
      "gratuityApplicable",
      "epsApplicable",
      "hasUAN",
      "hasESIIP",
    ];
    const newTouchedFields = {};
    allFields.forEach((field) => {
      newTouchedFields[field] = true;
    });
    setTouchedFields(newTouchedFields);


    // Validate all filters
    const errors = validateFilters(filters);
    setFormErrors(errors);


    // Check if there are any errors
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) {
      console.log("Validation errors:", errors);
      return;
    }


    try {
      const params = buildApiParamsFromFilters(filters);
      if (payslipMonthYear && typeof payslipMonthYear === "string" && payslipMonthYear.trim()) {
        params.payslipMonthYear = payslipMonthYear.trim();
      }
      const resp = await employeeService.getEmployees(params);


      let employeesData = [];
      if (!resp) employeesData = [];
      else if (Array.isArray(resp)) employeesData = resp;
      else if (resp.data && Array.isArray(resp.data)) employeesData = resp.data;
      else if (resp.employees && Array.isArray(resp.employees))
        employeesData = resp.employees;
      else if (resp.success && Array.isArray(resp.data))
        employeesData = resp.data;
      else employeesData = [];

      const selectedIds = new Set(
        (filters.selectedEmployeeIds || []).map((id) => String(id))
      );
      if (selectedIds.size > 0) {
        employeesData = employeesData.filter((emp) => {
          const candidateIds = [
            emp?.id,
            emp?.employeeId,
            emp?.userId,
            emp?.empCode,
          ]
            .map((value) => String(value ?? "").trim())
            .filter(Boolean);
          return candidateIds.some((id) => selectedIds.has(id));
        });
      }


      if (typeof onApply === "function") {
        onApply(employeesData, filters, params);
      }
    } catch (err) {
      console.error("Filters: failed to fetch employees with filters", err);
      if (typeof onApply === "function") {
        onApply([], filters, {});
      }
    }
  };


  // Focus management
  const modalRef = React.useRef(null);
  React.useEffect(() => {
    const el = modalRef.current;
    if (el) {
      const focusable = el.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const currentlyFocused = document.activeElement;


      if (focusable && !el.contains(currentlyFocused)) {
        focusable.focus();
      }
    }


    const onKey = (e) => {
      if (e.key === "Escape" && !e.target.matches("input, textarea, select")) {
        if (typeof onClose === "function") onClose();
      }
    };


    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);


  // Check if form has errors
  const hasErrors = Object.keys(formErrors).length > 0;
  const isFieldSelected = (fieldName, value) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) {
      const normalized = normalizeMultiSelectValues(value);
      return normalized.length > 0;
    }
    const v = String(value);
    if (v === "") return false;
    if (fieldName === "dateFilterType") {
      return v !== "all";
    }
    return true;
  };

  const getFieldBorderClass = (fieldName, value) => {
    if (formErrors[fieldName]) return "border-red-500";
    if (isFieldSelected(fieldName, value)) {
      return "border-emerald-500 dark:border-emerald-400";
    }
    return "";
  };

  const getFieldTextClass = (fieldName, value) => {
    if (!isFieldSelected(fieldName, value)) return "";
    return darkMode ? "text-emerald-400" : "text-emerald-700";
  };

  const getLabelTextClass = (fieldName, value) => {
    if (formErrors[fieldName]) return "text-red-500";
    if (isFieldSelected(fieldName, value)) {
      return darkMode ? "text-emerald-400" : "text-emerald-700";
    }
    return darkMode ? "text-gray-400" : "text-gray-500";
  };

  const findNameById = (list, id) => {
    const arr = Array.isArray(list) ? list : [];
    const item = arr.find((x) => String(x?.id) === String(id));
    return item?.name || "";
  };

  const summarizeMultiSelection = (list, value) => {
    const normalized = normalizeMultiSelectValues(value);
    if (normalized.length === 0) return "";
    if (normalized.includes(MULTI_SELECT_ALL_VALUE)) return "All";
    const labels = normalized
      .map((id) => findNameById(list, id) || String(id))
      .filter(Boolean);
    if (labels.length <= 2) return labels.join(", ");
    return `${labels[0]}, ${labels[1]} +${labels.length - 2} more`;
  };

  const getSelectedSummary = () => {
    const items = [];
    const selectedEmployees = Array.isArray(filters.selectedEmployees)
      ? filters.selectedEmployees
      : [];
    if (selectedEmployees.length > 0) {
      items.push({
        label: "Employees",
        value: `${selectedEmployees.length} selected`,
      });
    }
    if (isFieldSelected("branch", filters.branch)) {
      items.push({
        label: "Branch",
        value: summarizeMultiSelection(branches, filters.branch),
      });
    }
    if (isFieldSelected("department", filters.department)) {
      items.push({
        label: "Department",
        value: summarizeMultiSelection(departments, filters.department),
      });
    }
    if (isFieldSelected("designation", filters.designation)) {
      items.push({
        label: "Designation",
        value: summarizeMultiSelection(designations, filters.designation),
      });
    }
    if (isFieldSelected("grade", filters.grade)) {
      items.push({
        label: "Grade",
        value: summarizeMultiSelection(grades, filters.grade),
      });
    }
    if (isFieldSelected("category", filters.category)) {
      items.push({
        label: "Category",
        value: summarizeMultiSelection(categories, filters.category),
      });
    }
    if (isFieldSelected("subDepartment", filters.subDepartment)) {
      items.push({
        label: "Sub Department",
        value: summarizeMultiSelection(subDepartments, filters.subDepartment),
      });
    }
    if (isFieldSelected("bloodGroup", filters.bloodGroup)) {
      items.push({ label: "Blood Group", value: String(filters.bloodGroup) });
    }
    if (isFieldSelected("userStatus", filters.userStatus)) {
      items.push({ label: "User Status", value: String(filters.userStatus) });
    }
    if (isFieldSelected("gender", filters.gender)) {
      items.push({ label: "Gender", value: String(filters.gender) });
    }
    if (isFieldSelected("reportingManagerId", filters.reportingManagerId)) {
      items.push({
        label: "Reporting Manager",
        value: String(filters.reportingManagerId),
      });
    }
    if (isFieldSelected("employmentType", filters.employmentType)) {
      const employmentLabels = {
        "full-time": "Full Time",
        external: "External",
      };
      const normalized = normalizeMultiSelectValues(filters.employmentType);
      items.push({
        label: "Employment Type",
        value: normalized.includes(MULTI_SELECT_ALL_VALUE)
          ? "All"
          : normalized
              .map((value) => employmentLabels[value] || value)
              .filter(Boolean)
              .join(", "),
      });
    }
    if (isFieldSelected("status", filters.status)) {
      const statusLabels = {
        probation: "Probation",
        confirmed: "Confirmed",
        resigned: "Resigned",
        relieved: "Relieved",
        terminated: "Terminated",
      };
      const normalized = normalizeMultiSelectValues(filters.status);
      items.push({
        label: "Status",
        value: normalized.includes(MULTI_SELECT_ALL_VALUE)
          ? "All"
          : normalized
              .map((value) => statusLabels[value] || value)
              .filter(Boolean)
              .join(", "),
      });
    }
    if (isFieldSelected("dateFilterType", filters.dateFilterType)) {
      items.push({ label: "Date Filter", value: String(filters.dateFilterType) });
    }
    if (isFieldSelected("pfApplicable", filters.pfApplicable)) {
      items.push({ label: "PF", value: String(filters.pfApplicable) });
    }
    if (isFieldSelected("epsApplicable", filters.epsApplicable)) {
      items.push({ label: "EPS", value: String(filters.epsApplicable) });
    }
    if (isFieldSelected("esiApplicable", filters.esiApplicable)) {
      items.push({ label: "ESI", value: String(filters.esiApplicable) });
    }
    if (isFieldSelected("search", filters.search)) {
      items.push({ label: "Search", value: "…" });
    }
    return items.filter((x) => x.value);
  };

  const renderMultiSelectField = ({
    keyName,
    label,
    options,
    allLabel,
    placeholder = "Select",
    getOptionLabel = (item) => item?.name || "",
  }) => {
    const selectedValues = normalizeMultiSelectValues(filters[keyName]);
    const allSelected = selectedValues.includes(MULTI_SELECT_ALL_VALUE);
    const optionMap = new Map(
      (Array.isArray(options) ? options : []).map((option) => [
        String(option.id),
        getOptionLabel(option),
      ])
    );
    const selectedDisplayText =
      selectedValues.length === 0
        ? placeholder
        : allSelected
          ? allLabel
          : (() => {
              const labels = selectedValues
                .map((value) => optionMap.get(String(value)) || String(value))
                .filter(Boolean);
              if (labels.length <= 2) return labels.join(", ");
              return `${labels[0]}, ${labels[1]} +${labels.length - 2} more`;
            })();

    return (
      <div className="relative" data-multi-select-root="true">
        <button
          type="button"
          onClick={() =>
            setOpenMultiSelectKey((prev) => (prev === keyName ? null : keyName))
          }
          className={`block min-h-[42px] w-full rounded-md border px-2.5 pb-2.5 pt-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-redhalo-red/25 ${
            darkMode
              ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
              : "bg-white border-gray-300 text-gray-900"
          } ${getFieldBorderClass(keyName, selectedValues)}`}
        >
          <span
            className={`line-clamp-1 ${
              selectedValues.length
                ? getFieldTextClass(keyName, selectedValues)
                : darkMode
                  ? "text-gray-400"
                  : "text-gray-500"
            }`}
          >
            {selectedDisplayText}
          </span>
        </button>
        <label
          className={`absolute left-1 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform px-2 rounded-sm text-sm duration-300 ${
            darkMode ? "bg-[#3C3C3C]" : "bg-white"
          } ${getLabelTextClass(keyName, selectedValues)}`}
        >
          {label}
        </label>
        {openMultiSelectKey === keyName && (
          <div
            className={`absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border shadow-lg ${
              darkMode ? "border-[#4C4C4C] bg-[#2F2F2F]" : "border-gray-200 bg-white"
            }`}
          >
            <button
              type="button"
              onClick={() => toggleMultiSelectValue(keyName, MULTI_SELECT_ALL_VALUE)}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                darkMode ? "text-[#E0E0E0] hover:bg-[#3A3A3A]" : "text-gray-800 hover:bg-gray-50"
              }`}
            >
              <input
                type="checkbox"
                readOnly
                checked={allSelected}
                className="h-4 w-4"
              />
              <span>{allLabel}</span>
            </button>
            {(Array.isArray(options) ? options : []).map((option) => {
              const optionValue = String(option.id);
              const checked = selectedValues.includes(optionValue);
              const disabled = allSelected;
              return (
                <button
                  key={`${keyName}-${option.id}`}
                  type="button"
                  disabled={disabled}
                  onClick={() => toggleMultiSelectValue(keyName, optionValue)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                    disabled
                      ? darkMode
                        ? "cursor-not-allowed text-gray-500"
                        : "cursor-not-allowed text-gray-400"
                      : darkMode
                        ? "text-[#E0E0E0] hover:bg-[#3A3A3A]"
                        : "text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    readOnly
                    checked={checked}
                    disabled={disabled}
                    className="h-4 w-4"
                  />
                  <span>{getOptionLabel(option)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };


  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-[hsl(var(--ds-fg)/0.45)] backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-label="Report Filters"
    >
      <div
        ref={modalRef}
        className="flex h-[80vh] max-h-[90vh] w-[95%] max-w-6xl flex-col overflow-hidden rounded-[var(--ds-radius-xl)] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-panel)]"
      >
        {/* Fixed Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[hsl(var(--ds-border))] px-6 py-4">
          <h2 className="ds-h2 text-[hsl(var(--ds-fg))]">Filter</h2>
          <button
            onClick={onClose}
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--ds-muted-fg))] transition-colors hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
            aria-label="Close filters"
          >
            <DsIcon icon={X} size="md" />
          </button>
        </div>


        {/* Scrollable Content */}
        <fieldset
          disabled={loading}
          className="flex-1 overflow-y-auto overflow-x-hidden p-6"
        >
          {/* Master Data */}
          <div className="mb-8">
            <h3 className="ds-h3 mb-4 text-[hsl(var(--ds-fg))]">
              Master Data
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {renderMultiSelectField({
                keyName: "branch",
                label: "Branch",
                options: branches,
                allLabel: "All Branches",
                placeholder: "Select one or more branches",
              })}


              {renderMultiSelectField({
                keyName: "department",
                label: "Department",
                options: departments,
                allLabel: "All Departments",
                placeholder: "Select one or more departments",
              })}


              {renderMultiSelectField({
                keyName: "designation",
                label: "Designation",
                options: designations,
                allLabel: "All Designations",
                placeholder: "Select one or more designations",
              })}


              {/* Additional master filters: Grade, Category, Sub-department */}
              {renderMultiSelectField({
                keyName: "grade",
                label: "Grade",
                options: grades,
                allLabel: "All Grades",
                placeholder: "Select one or more grades",
              })}


              {renderMultiSelectField({
                keyName: "category",
                label: "Category",
                options: categories,
                allLabel: "All Categories",
                placeholder: "Select one or more categories",
              })}


              {renderMultiSelectField({
                keyName: "subDepartment",
                label: "Sub Department",
                options: subDepartments,
                allLabel: "All Sub Departments",
                placeholder: "Select one or more sub departments",
              })}
            </div>
          </div>


          {/* Employment Details */}
          <div className="mb-8">
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              Employment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {renderMultiSelectField({
                keyName: "employmentType",
                label: "Employment Type",
                options: [
                  { id: "full-time", name: "Full Time" },
                  { id: "external", name: "External" },
                ],
                allLabel: "All Types",
                placeholder: "Select one or more employment types",
              })}


              {renderMultiSelectField({
                keyName: "status",
                label: "Employment Status",
                options: [
                  { id: "probation", name: "Probation" },
                  { id: "confirmed", name: "Confirmed" },
                  { id: "resigned", name: "Resigned" },
                  { id: "relieved", name: "Relieved" },
                  { id: "terminated", name: "Terminated" },
                ],
                allLabel: "All Status",
                placeholder: "Select one or more statuses",
              })}
            </div>
          </div>


          {/* Search & User Filter */}
          <div className="mb-8">
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              Search & User Filter
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="relative md:col-span-2 lg:col-span-3" ref={employeePickerRef}>
                <div
                  className={`flex min-h-[44px] w-full flex-wrap items-center gap-2 rounded-md border px-2 py-1 focus-within:ring-2 focus-within:ring-redhalo-red/25 ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C]"
                      : "bg-white border-gray-300"
                  } ${getFieldBorderClass("search", filters.search)}`}
                >
                  {Array.isArray(filters.selectedEmployees) &&
                    filters.selectedEmployees.map((emp) => (
                      <span
                        key={emp.id}
                        className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${
                          darkMode
                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                            : "border-emerald-300 bg-emerald-50 text-emerald-700"
                        }`}
                      >
                        <span className="truncate">
                          {emp.empCode ? `${displayLabel(emp.empCode, "")} - ` : ""}
                          {employeeDisplayName(emp)}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeSelectedEmployee(emp.id)}
                          className={`ml-1 rounded-full px-1 transition ${
                            darkMode ? "hover:bg-emerald-400/20" : "hover:bg-emerald-100"
                          }`}
                          aria-label={`Remove ${employeeDisplayName(emp)}`}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  <input
                    type="text"
                    name="employee_filter_query"
                    id="admin-filters-search"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    data-lpignore="true"
                    value={filters.search || ""}
                    onChange={handleChange("search")}
                    onFocus={() => {
                      if (String(filters.search || "").trim().length >= MIN_EMPLOYEE_QUERY_LEN) {
                        setShowEmployeeSuggestions(true);
                        setActiveEmployeeSuggestionIndex(
                          employeeSuggestions.length > 0 ? 0 : -1
                        );
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        setShowEmployeeSuggestions(false);
                        setActiveEmployeeSuggestionIndex(-1);
                      }
                      if (
                        e.key === "ArrowDown" &&
                        showEmployeeSuggestions &&
                        employeeSuggestions.length > 0
                      ) {
                        e.preventDefault();
                        setActiveEmployeeSuggestionIndex((prev) =>
                          prev < employeeSuggestions.length - 1 ? prev + 1 : 0
                        );
                        return;
                      }
                      if (
                        e.key === "ArrowUp" &&
                        showEmployeeSuggestions &&
                        employeeSuggestions.length > 0
                      ) {
                        e.preventDefault();
                        setActiveEmployeeSuggestionIndex((prev) =>
                          prev > 0 ? prev - 1 : employeeSuggestions.length - 1
                        );
                        return;
                      }
                      if (
                        e.key === "Enter" &&
                        showEmployeeSuggestions &&
                        employeeSuggestions.length > 0
                      ) {
                        e.preventDefault();
                        const index =
                          activeEmployeeSuggestionIndex >= 0
                            ? activeEmployeeSuggestionIndex
                            : 0;
                        addSelectedEmployee(employeeSuggestions[index]);
                      }
                    }}
                    className={`min-w-[240px] flex-1 border-0 bg-transparent px-1 py-1 text-sm focus:outline-none ${
                      darkMode ? "text-[#E0E0E0]" : "text-gray-900"
                    } ${getFieldTextClass("search", filters.search)}`}
                    placeholder="Type employee code, name, or email"
                  />
                </div>
                <p className={`mt-1 text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                  Minimum {MIN_EMPLOYEE_QUERY_LEN} characters. Press Enter to quickly select first result.
                </p>
                {Array.isArray(filters.selectedEmployees) && filters.selectedEmployees.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedEmployees([])}
                    className={`mt-1 text-xs underline ${
                      darkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-600 hover:text-gray-800"
                    }`}
                  >
                    Clear selected employees
                  </button>
                )}

                {showEmployeeSuggestions && (
                  <div
                    className={`absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-md border shadow-lg ${
                      darkMode ? "border-[#4C4C4C] bg-[#2F2F2F]" : "border-gray-200 bg-white"
                    }`}
                  >
                    {employeeSuggestionsLoading ? (
                      <div className={`px-3 py-2 text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                        Searching employees...
                      </div>
                    ) : employeeSuggestions.length > 0 ? (
                      employeeSuggestions.map((emp, index) => (
                        <button
                          key={emp.id}
                          type="button"
                          onClick={() => addSelectedEmployee(emp)}
                          className={`flex w-full items-start justify-between px-3 py-2 text-left text-sm transition ${
                            activeEmployeeSuggestionIndex === index
                              ? darkMode
                                ? "bg-[#3A3A3A]"
                                : "bg-gray-100"
                              : ""
                          } ${
                            darkMode
                              ? "text-[#E0E0E0] hover:bg-[#3A3A3A]"
                              : "text-gray-800 hover:bg-gray-50"
                          }`}
                        >
                          <div className="min-w-0">
                            <div className="truncate font-medium">{employeeDisplayName(emp)}</div>
                            <div className={`truncate text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                              {[emp.empCode, emp.email].filter(Boolean).join(" | ")}
                            </div>
                          </div>
                          <span className={`ml-3 shrink-0 text-xs ${darkMode ? "text-emerald-300" : "text-emerald-700"}`}>
                            Select
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className={`px-3 py-2 text-sm ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                        No matching employees found
                      </div>
                    )}
                  </div>
                )}
              </div>


              <div className="relative">
                <select
                  value={filters.gender || ""}
                  onChange={handleChange("gender")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("gender", filters.gender)} ${getFieldTextClass("gender", filters.gender)}`}
                >
                  <option value="">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("gender", filters.gender)}`}
                >
                  Gender
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.userStatus || ""}
                  onChange={handleChange("userStatus")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("userStatus", filters.userStatus)} ${getFieldTextClass("userStatus", filters.userStatus)}`}
                >
                  <option value="">All User Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("userStatus", filters.userStatus)}`}
                >
                  User Status
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.bloodGroup || ""}
                  onChange={handleChange("bloodGroup")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("bloodGroup", filters.bloodGroup)} ${getFieldTextClass("bloodGroup", filters.bloodGroup)}`}
                >
                  <option value="">All Blood Groups</option>
                  {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(
                    (bg) => (
                      <option key={bg} value={bg}>
                        {bg}
                      </option>
                    )
                  )}
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("bloodGroup", filters.bloodGroup)}`}
                >
                  Blood Group
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.reportingManagerId || ""}
                  onChange={handleChange("reportingManagerId")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("reportingManagerId", filters.reportingManagerId)} ${getFieldTextClass("reportingManagerId", filters.reportingManagerId)}`}
                >
                  <option value="">All Reporting Managers</option>
                  {(reportingManagers?.data || reportingManagers).map((rm) => (
                    <option key={rm.id} value={rm.id}>
                      {rm.empCode} - {rm.user?.name || rm.user?.name}
                    </option>
                  ))}
                </select>
                <label
                  className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${
                    darkMode ? "bg-[#3C3C3C]" : "bg-white"
                  } ${getLabelTextClass("reportingManagerId", filters.reportingManagerId)}`}
                >
                  Reporting Manager
                </label>
              </div>
            </div>
          </div>


          {/* Date Filter & Statutory */}
          <div className="mb-8">
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              Date Filter
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="relative">
                <select
                  value={filters.dateFilterType}
                  onChange={handleChange("dateFilterType")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("dateFilterType", filters.dateFilterType)} ${getFieldTextClass("dateFilterType", filters.dateFilterType)}`}
                >
                  <option value="all">All</option>
                  <option value="dateOfJoin">Date of Joining</option>
                  <option value="dateOfRelieving">Date of Relieving</option>
                  <option value="confirmationDate">Confirmation Date</option>
                  <option value="resignationDate">Resignation Date</option>
                  <option value="terminationDate">Termination Date</option>
                  <option value="dateOfBirth">Date of Birth</option>
                  <option value="extendedProbationDate">
                    Extended Probation Date
                  </option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("dateFilterType", filters.dateFilterType)}`}>
                  Date Filter Type
                </label>
              </div>


              <DatePicker
                value={filters.reportDateFrom || ""}
                onChange={handleChange("reportDateFrom")}
                onBlur={() => handleFieldBlur("reportDateFrom")}
                disabled={filters.dateFilterType === "all"}
                darkMode={darkMode}
                label="From Date"
                max={filters.reportDateTo || ""}
                maxErrorText="From Date cannot be greater than To Date"
                error={formErrors.dateError}
              />


              <DatePicker
                value={filters.reportDateTo || ""}
                onChange={handleChange("reportDateTo")}
                onBlur={() => handleFieldBlur("reportDateTo")}
                disabled={filters.dateFilterType === "all"}
                darkMode={darkMode}
                label="To Date"
                min={filters.reportDateFrom}
                minErrorText="To Date cannot be less than From Date"
                error={formErrors.dateError}
              />
            </div>


            {formErrors.dateError && (
              <div className="text-sm text-red-500 mt-2">
                {formErrors.dateError}
              </div>
            )}


            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              Statutory Applicability
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative">
                <select
                  value={filters.pfApplicable}
                  onChange={handleChange("pfApplicable")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("pfApplicable", filters.pfApplicable)} ${getFieldTextClass("pfApplicable", filters.pfApplicable)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("pfApplicable", filters.pfApplicable)}`}>
                  PF Applicable
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.epsApplicable}
                  onChange={handleChange("epsApplicable")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("epsApplicable", filters.epsApplicable)} ${getFieldTextClass("epsApplicable", filters.epsApplicable)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("epsApplicable", filters.epsApplicable)}`}>
                  EPS Applicable
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.esiApplicable}
                  onChange={handleChange("esiApplicable")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("esiApplicable", filters.esiApplicable)} ${getFieldTextClass("esiApplicable", filters.esiApplicable)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("esiApplicable", filters.esiApplicable)}`}>
                  ESI Applicable
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.ceilingPf}
                  onChange={handleChange("ceilingPf")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("ceilingPf", filters.ceilingPf)} ${getFieldTextClass("ceilingPf", filters.ceilingPf)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("ceilingPf", filters.ceilingPf)}`}>
                  Ceiling PF
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.hasUAN}
                  onChange={handleChange("hasUAN")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("hasUAN", filters.hasUAN)} ${getFieldTextClass("hasUAN", filters.hasUAN)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("hasUAN", filters.hasUAN)}`}>
                  Has UAN
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.hasESIIP}
                  onChange={handleChange("hasESIIP")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("hasESIIP", filters.hasESIIP)} ${getFieldTextClass("hasESIIP", filters.hasESIIP)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("hasESIIP", filters.hasESIIP)}`}>
                  Has ESI IP
                </label>
              </div>
            </div>
          </div>


          {/* PT and Gratuity applicability */}
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative">
                <select
                  value={filters.ptApplicable}
                  onChange={handleChange("ptApplicable")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("ptApplicable", filters.ptApplicable)} ${getFieldTextClass("ptApplicable", filters.ptApplicable)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("ptApplicable", filters.ptApplicable)}`}>
                  PT Applicable
                </label>
              </div>


              <div className="relative">
                <select
                  value={filters.gratuityApplicable}
                  onChange={handleChange("gratuityApplicable")}
                  className={`block px-2.5 pb-2.5 pt-4 w-full text-sm rounded-md border appearance-none focus:outline-none focus:ring-0 peer ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${getFieldBorderClass("gratuityApplicable", filters.gratuityApplicable)} ${getFieldTextClass("gratuityApplicable", filters.gratuityApplicable)}`}
                >
                  <option value="">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
                <label className={`absolute text-sm duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] px-2 left-1 rounded-sm ${darkMode ? "bg-[#3C3C3C]" : "bg-white"} ${getLabelTextClass("gratuityApplicable", filters.gratuityApplicable)}`}>
                  Gratuity Applicable
                </label>
              </div>
            </div>
          </div>


          {/* CTC Range */}
          <div className="mb-8">
            <h3
              className={`text-lg font-semibold mb-4 ${
                darkMode ? "text-[#E0E0E0]" : "text-gray-900"
              }`}
            >
              CTC Range
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    darkMode ? "text-[#E0E0E0]" : "text-gray-700"
                  } ${getFieldTextClass("ctcFrom", filters.ctcFrom)}`}
                >
                  CTC From (₹)
                </label>
                <input
                  type="number"
                  value={filters.ctcFrom || ""}
                  onChange={handleChange("ctcFrom")}
                  onBlur={() => handleFieldBlur("ctcFrom")}
                  placeholder="Enter Minimum CTC"
                  className={`block w-full px-3 py-2 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${
                    formErrors.ctcFrom || formErrors.ctcError
                      ? "border-red-500"
                      : getFieldBorderClass("ctcFrom", filters.ctcFrom)
                  } ${getFieldTextClass("ctcFrom", filters.ctcFrom)}`}
                  min="0"
                  step="0.01"
                />
                {formErrors.ctcFrom && (
                  <div className="text-sm text-red-500 mt-1">
                    {formErrors.ctcFrom}
                  </div>
                )}
              </div>


              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    darkMode ? "text-[#E0E0E0]" : "text-gray-700"
                  } ${getFieldTextClass("ctcTo", filters.ctcTo)}`}
                >
                  CTC To (₹)
                </label>
                <input
                  type="number"
                  value={filters.ctcTo || ""}
                  onChange={handleChange("ctcTo")}
                  onBlur={() => handleFieldBlur("ctcTo")}
                  placeholder="Enter Maximum CTC"
                  className={`block w-full px-3 py-2 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 ${
                    darkMode
                      ? "bg-[#3C3C3C] border-[#4C4C4C] text-[#E0E0E0]"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${
                    formErrors.ctcTo || formErrors.ctcError
                      ? "border-red-500"
                      : getFieldBorderClass("ctcTo", filters.ctcTo)
                  } ${getFieldTextClass("ctcTo", filters.ctcTo)}`}
                  min="0"
                  step="0.01"
                />
                {formErrors.ctcTo && (
                  <div className="text-sm text-red-500 mt-1">
                    {formErrors.ctcTo}
                  </div>
                )}
              </div>
            </div>
            {formErrors.ctcError &&
              !formErrors.ctcFrom &&
              !formErrors.ctcTo && (
                <div className="text-sm text-red-500 mt-2">
                  {formErrors.ctcError}
                </div>
              )}
          </div>
        </fieldset>


        {/* Fixed Action Buttons */}
        <div className="flex justify-between items-center p-6 border-t flex-shrink-0">
          <div className="min-w-0 pr-4">
            {(() => {
              const selected = getSelectedSummary();
              const employeeCount = Array.isArray(filters.selectedEmployeeIds)
                ? filters.selectedEmployeeIds.length
                : 0;
              if (!selected.length && employeeCount === 0) return null;
              return (
                <div className="space-y-1">
                  {employeeCount > 0 && (
                    <div
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        darkMode
                          ? "bg-emerald-500/20 text-emerald-300"
                          : "bg-emerald-100 text-emerald-800"
                      }`}
                    >
                      {employeeCount} employee{employeeCount > 1 ? "s" : ""} selected
                    </div>
                  )}
                  <div className={`text-sm ${darkMode ? "text-emerald-400" : "text-emerald-700"} truncate`}>
                    <span className={darkMode ? "text-gray-400" : "text-gray-600"}>
                      Selected:
                    </span>
                    {selected.map((s, idx) => (
                      <span key={`${s.label}-${idx}`}>
                        {idx > 0 ? ", " : " "}
                        {s.label}
                      </span>
                    ))}
                  </div>
                  {employeeCount > 0 && (
                    <div className={`text-xs truncate ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                      {filters.selectedEmployees
                        .map((emp) => formatEmployeeOptionLabel(emp))
                        .slice(0, 2)
                        .join(", ")}
                      {employeeCount > 2 ? ` +${employeeCount - 2} more` : ""}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="flex gap-3">
            <DsButton type="button" variant="outline" onClick={handleClear}>
              Clear
            </DsButton>
            <DsButton type="button" onClick={handleApply} disabled={hasErrors}>
              Apply
            </DsButton>
          </div>
        </div>
      </div>
    </div>
  );
}


